import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  choosePostRaceNoObjective,
  getPostRaceState,
  markPostRaceShown,
  postponePostRace,
  PostRaceState,
} from '@services/postRaceService';
import { queryKeys } from './queryKeys';
import { useUser } from './useUser';

/**
 * PRD 8 — the post-race state. Enabled only for a V2 athlete, so a V1 one never makes the call and
 * the gate can stay mounted app-level for both worlds (D17).
 */
export const usePostRace = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery<PostRaceState | null>({
    queryKey: queryKeys.postRace,
    queryFn: getPostRaceState,
    enabled: !!user?.plan_v2_id,
    staleTime: 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.postRace });

  const shown = useMutation({ mutationFn: markPostRaceShown });
  const postpone = useMutation({ mutationFn: postponePostRace, onSuccess: invalidate });
  const chooseNoObjective = useMutation({ mutationFn: choosePostRaceNoObjective, onSuccess: invalidate });

  /**
   * PRD 8 §4 — the two choices, shared by the mandatory dialog and by the summary screen of the
   * competition drawer, so the two surfaces cannot drift.
   *
   * « Une nouvelle course » records **no** choice: the athlete may abandon the objective flow, and the
   * question must stay open. It only postpones, so the dialog does not nag again today; the choice is
   * stamped by the enrollment itself when it succeeds.
   */
  const choose = async (mode: 'race' | 'background') => {
    if (mode === 'race') {
      await postpone.mutateAsync();
      navigate('/new-objective');
      return;
    }
    await chooseNoObjective.mutateAsync();
  };

  return {
    state: data ?? null,
    choose,
    isLoading,
    markShown: shown.mutateAsync,
    postpone: postpone.mutateAsync,
    chooseNoObjective: chooseNoObjective.mutateAsync,
    isSubmitting: postpone.isPending || chooseNoObjective.isPending,
  };
};
